/**
 * The Training screen: Planning on a practice fleet, with the Tutorial Center
 * docked beside it.
 *
 * Entering opens the sandbox; leaving closes it. The predecessor's practice
 * mode only hid real aircraft in the browser, so a trainee one click from the
 * wrong tab could still command one. Here the server narrows the trainee's
 * control to the sandbox for as long as it is open, so the sandbox must not
 * outlive the screen: an operator who wanders back to Command to fly their own
 * aircraft gets their commands back without having to remember to end anything.
 */

import { useEffect, useRef } from 'react';

import { useCurrentUser } from '@/stores/authStore';
import { useMapStore } from '@/stores/mapStore';
import { useWorkspaceStore } from '@/stores/workspaceStore';
import { aorById } from '@/map/aors';
import { PlanningView, planningInitialCamera } from '@/features/planning/PlanningView';
import { usePlanDocks, useTutorialFloats } from '@/features/planning/planDocks';

import { usePracticeStore } from './practiceStore';
import { TutorialCenter } from './TutorialCenter';
import { useTutorialStore } from './tutorialStore';

/** The server's lease is five minutes; renewing every minute rides out a tab
 *  the browser throttles to one timer a minute without ever cutting it fine. */
const RENEW_MS = 60_000;

/** How long a close waits for a remount. React mounts effects twice in
 *  development, and a close that went straight out would tear the fleet down
 *  and spawn a fresh one every time the screen was opened. */
const CLOSE_GRACE_MS = 500;

let pendingClose: number | null = null;

function cancelPendingClose(): void {
  if (pendingClose !== null) {
    window.clearTimeout(pendingClose);
    pendingClose = null;
  }
}

function scheduleClose(): void {
  cancelPendingClose();
  pendingClose = window.setTimeout(() => {
    pendingClose = null;
    void usePracticeStore.getState().close();
  }, CLOSE_GRACE_MS);
}

function PracticeBanner() {
  return (
    <div
      role="status"
      className="glass-pill pointer-events-none absolute left-1/2 top-[132px] z-10 flex max-w-[min(460px,calc(100%-700px))] min-w-[300px] -translate-x-1/2 items-center gap-2.5 rounded-full border px-4 py-2"
      style={{ borderColor: 'color-mix(in oklch, var(--caution) 55%, transparent)' }}
    >
      <span
        className="flex-none rounded-full px-2 py-[2px] font-mono text-[8.5px] font-bold tracking-wide"
        style={{ background: 'var(--caution)', color: '#1c1503' }}
      >
        PRACTICE
      </span>
      <span className="text-[10.5px] leading-snug text-white/75">
        Your commands reach only the practice fleet. Real aircraft stay on the map until you end
        practice.
      </span>
    </div>
  );
}

export function TrainingView() {
  const user = useCurrentUser();
  const hydrate = useTutorialStore((state) => state.hydrate);
  const aor = aorById(useWorkspaceStore((state) => state.aor));
  const setScreen = useWorkspaceStore((state) => state.setScreen);
  // Where the Planning map will open, which is where the fleet should appear.
  const where = useRef(planningInitialCamera(useMapStore.getState().camera, aor).center);

  useEffect(() => {
    if (user) hydrate(user.username);
  }, [user, hydrate]);

  // The lessons are the point of the screen, so they open first; opening ROUTE
  // or the AI operator over them folds them to their pill (see planDocks).
  useEffect(() => {
    usePlanDocks.getState().show('tutorial', 'full');
  }, []);

  useEffect(() => {
    cancelPendingClose();
    useTutorialStore.getState().beginSession();
    const { lat, lon } = where.current;
    const practice = usePracticeStore.getState();
    if (practice.status === 'idle' || practice.status === 'refused') void practice.open(lat, lon);
    const timer = window.setInterval(() => void usePracticeStore.getState().renew(lat, lon), RENEW_MS);
    return () => {
      window.clearInterval(timer);
      scheduleClose();
    };
  }, []);

  const retry = () => {
    const { lat, lon } = where.current;
    void usePracticeStore.getState().open(lat, lon);
  };

  // A 360px column beside Planning's own two columns needs about 1440px: at
  // 1280 the tool palette sat on both plan panels, at 768 the map was 380px
  // wide, and on a phone it was negative. Below that Planning gets the whole
  // screen and the Tutorial Center floats over it, folding to a pill that also
  // carries the PRACTICE mark the banner would. Where it floats, and what it
  // gives way to, is in styles.css and planDocks.ts.
  const compact = useTutorialFloats();

  return (
    <div className={compact ? 'training-float absolute inset-0' : 'training-column absolute inset-0'}>
      <div className={compact ? 'absolute inset-0' : 'absolute inset-y-0 left-0 right-[var(--tutorial-col)]'}>
        <PlanningView />
        {compact ? null : <PracticeBanner />}
      </div>
      <TutorialCenter onRetry={retry} onEnd={() => setScreen('planning')} collapsible={compact} />
    </div>
  );
}

export default TrainingView;
